import { Injectable } from "@angular/core";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { filter, flatMap, map, throttleTime } from "rxjs/operators";
import { SearchClientService } from "@app2/clients/search-client.service";
import { Watchlist, WatchlistItem, WatchlistItems } from "@app2/type-defs/search/search-types";

export const watchlistServiceRequestThrottleTime = 1000;

@Injectable({
    providedIn: "root",
})
export class WatchlistService {
    private watchListItems$ = new ReplaySubject<Record<uuid, WatchlistItem[]>>(1);
    private watchListItems: Record<uuid, WatchlistItem[]> = {};
    private watchLists$ = new ReplaySubject<Watchlist[]>(1);

    private requestReload = new Subject<void>();

    constructor(private readonly searchService: SearchClientService,
                private readonly notificationsService: NotificationsService) {
        this.requestReload
            .pipe(
                throttleTime(watchlistServiceRequestThrottleTime),
            )
            .subscribe(() => this.reloadAllData());

        this.requestReload.next();
    }

    public reloadAllData(): void {
        this.reloadWatchlists()
            .then((watchLists: Watchlist[]) => Promise.all(watchLists.map(w => this.reloadWatchlistItemsForWatchlist(w.id))))
            .catch(error => {
                const message = getMessageForError(error.error, "WATCHLISTS_LOADING_ERROR");
                this.notificationsService.showError(message, error);
            });
    }

    public getWatchlistItemsForWatchlist$(id: uuid, reload = false): Observable<WatchlistItem[]> {
        if (reload) {
            this.requestReload.next();
        }

        return this.watchListItems$.asObservable()
            .pipe(map(items => items[id]),
                  // avoid emitting undefined because the items are loaded by watchlist; watchListItems$ emits even when
                  // *any* watchlistItems are fetched
                  filter(items => items !== undefined));
    }

    public reloadWatchlistItemsForWatchlist(id: uuid): Promise<WatchlistItems> {
        return this.searchService.getWatchlistItems(id).then(items => {

            this.watchListItems[id] = items.items;
            this.watchListItems$.next(this.watchListItems);

            return items;
        });
    }

    public getWatchlists$(reload = false): Observable<Watchlist[]> {
        if (reload) {
            this.requestReload.next();
        }
        return this.watchLists$.asObservable();
    }

    public getWatchlist$(id: uuid, reload = false): Observable<Watchlist> {
        if (reload) {
            this.requestReload.next();
        }
        return this.watchLists$.asObservable()
            .pipe(flatMap((watchlist: Watchlist[]) => watchlist))
            .pipe(filter((watchlist: Watchlist) => watchlist.id === id));
    }

    public reloadWatchlists(): Promise<Watchlist[]> {
        return this.searchService.getOrgWatchlists()
            .then((watchlists: Watchlist[]) => {
                this.watchLists$.next(watchlists);

                return watchlists;
            });
    }
}
